import { useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocalRuntime } from '@assistant-ui/react';
import type { ChatModelAdapter, AssistantRuntime, ThreadMessageLike } from '@assistant-ui/react';
import type { UIEvent } from '../types/ui-events';
import { getUserTimezone } from '../utils/timezone';
import { shouldAutoFocusInputs } from '../utils/constrained-device';
import { useAuth } from '../store/auth-context';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import {
  createAsyncInvokeRun,
  ActiveRunIdStore,
  activeFocusedRunIdStore,
} from './useAsyncInvokeAdapter';
import { useMeAgent } from './useAgents';
import { useLatest } from './useLatest';
import { getOrCreateConnection, closeConnection } from '../utils/direct-run-connection';

export interface AgentRuntimeOptions {
  agentId: string;
  /** Owning space for the agent's runs. Required — backend rule: all
   *  per-resource (run, artifact, …) reads + the invoke endpoint live
   *  under `/spaces/{spaceId}/...`. */
  spaceId: string | undefined;
  initialConversationId?: string;
  onConversationChange?: (conversationId: string) => void;
  initialMessages?: readonly ThreadMessageLike[];
  dispatchUIEvent?: (event: UIEvent) => void;
  /** Callback when a run starts/ends. Only used by builder. */
  onActiveRunId?: (runId: string | undefined) => void;
}

export interface AgentRuntimeResult {
  runtime: AssistantRuntime;
  conversationIdRef: React.MutableRefObject<string | undefined>;
  /** Reactive observable of this surface's active runId (undefined when idle). */
  activeRunIdStore: ActiveRunIdStore;
  /** Resolves a tool database ID to a human-readable name. */
  getToolName: (nameOrId: string) => string;
  /** Bridge for the composer's attachments context. The thread (which
   *  mounts the attachments provider downstream of this hook) writes the
   *  `consume` function here in an effect; the adapter reads it via this
   *  ref at invoke time. Leaving this unset is fine — surfaces without
   *  composer attachments just don't write to it. */
  consumeAttachmentsRef: React.MutableRefObject<
    (() => Promise<import('../types').FileRef[]>) | undefined
  >;
}

/**
 * Shared hook that creates a ChatModelAdapter + local runtime for
 * any agent. Used by every conversation surface (Builder, Sandbox,
 * Conversations) — pair with `useAgentRunEvents` to also catch
 * background runs initiated outside the adapter (e.g. AUI dispatch
 * actions).
 */
export function useAgentRuntime({
  agentId,
  spaceId,
  initialConversationId,
  onConversationChange,
  initialMessages,
  dispatchUIEvent,
  onActiveRunId,
}: AgentRuntimeOptions): AgentRuntimeResult {
  const { user } = useAuth();
  const { subscribe, isConnected } = usePlatformEvents();
  const { data: agent } = useMeAgent(agentId);
  const spaceIdRef = useLatest(spaceId);

  const conversationIdRef = useRef<string | undefined>(initialConversationId);
  // Per-surface reactive observable. `useAgentRunEvents` subscribes to it
  // so background-run thread.append calls can defer until this surface's
  // foreground stream completes.
  const activeRunIdStoreRef = useRef<ActiveRunIdStore>();
  if (!activeRunIdStoreRef.current) activeRunIdStoreRef.current = new ActiveRunIdStore();
  const activeRunIdStore = activeRunIdStoreRef.current;

  // Use useLatest for values that need to be read inside the adapter
  // without causing re-creation of the adapter on every change.
  const onConversationChangeRef = useLatest(onConversationChange);
  const userTimezoneRef = useLatest(user?.timezone);
  const subscribeRef = useLatest(subscribe);
  const isConnectedRef = useLatest(isConnected);
  const dispatchRef = useLatest(dispatchUIEvent);
  const onActiveRunIdRef = useLatest(onActiveRunId);

  // Build tool name map from agent's tools
  const toolNameMapRef = useRef<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    const m = new Map<string, string>();
    if (agent?.tools) {
      for (const t of agent.tools) m.set(t.toolId, t.name);
    }
    toolNameMapRef.current = m;
  }, [agent?.tools]);

  // Reset conversationId when the agent changes (but not on initial mount,
  // which would wipe the initialConversationId passed for restored sessions)
  const prevAgentIdRef = useRef(agentId);
  useEffect(() => {
    if (prevAgentIdRef.current !== agentId) {
      // Release the prior conversation's direct-run socket — it's bound to a
      // warm micro-VM that no longer matches the new agent/conversation.
      closeConnection(conversationIdRef.current);
      conversationIdRef.current = undefined;
      prevAgentIdRef.current = agentId;
    }
  }, [agentId]);

  // Bind this surface's direct-run socket to its lifetime. On mount, (re-)acquire
  // the connection for the current conversation; on unmount, release it.
  //
  // The release is DEFERRED in the registry, and acquiring cancels a pending
  // release. That makes the pair resilient to React StrictMode's synthetic
  // mount→unmount→remount in dev: the cleanup schedules a close, the immediate
  // re-mount's acquire cancels it, so the eagerly-prewarmed socket survives
  // instead of being torn down and replaced by a cold one on the first send.
  // A real unmount runs only the cleanup, so the deferred close still fires.
  useEffect(() => {
    const cid = conversationIdRef.current;
    if (cid) getOrCreateConnection(cid, { getSpaceId: () => spaceIdRef.current, agentId });
    return () => closeConnection(conversationIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setConversationId = useCallback((id: string) => {
    conversationIdRef.current = id;
    onConversationChangeRef.current?.(id);
  }, [onConversationChangeRef]);

  // Ref to the runtime — used by removeLastExchange which is created before
  // the runtime exists (adapter is passed to useLocalRuntime).
  const runtimeRef = useRef<AssistantRuntime | undefined>(undefined);
  // Bridge for the composer attachments provider (mounted downstream by
  // ConversationThread). The adapter reads this via closure at invoke time.
  const consumeAttachmentsRef = useRef<
    (() => Promise<import('../types').FileRef[]>) | undefined
  >(undefined);

  const adapter = useMemo<ChatModelAdapter>(
    () => ({
      run: createAsyncInvokeRun({
        agentId,
        getSpaceId: () => spaceIdRef.current,
        getConversationId: () => conversationIdRef.current,
        setConversationId,
        getUserTimezone: () => getUserTimezone(userTimezoneRef.current ?? undefined),
        getSubscribe: () => subscribeRef.current,
        onActiveRunId: (runId) => {
          activeRunIdStore.set(runId);
          // Mirror to the global focused-run observable so the
          // context-utilization bar (a single shared surface-level
          // indicator) shows the most recently active run.
          activeFocusedRunIdStore.set(runId);
          onActiveRunIdRef.current?.(runId);
        },
        dispatchUIEvent: (event) => dispatchRef.current?.(event),
        getToolName: (id) => toolNameMapRef.current.get(id) ?? id,
        // Read the ref at INVOKE time, not at adapter-creation time — the
        // composer attachments provider mounts downstream and registers its
        // `consume` after this useMemo runs.
        consumeAttachments: async () => {
          const fn = consumeAttachmentsRef.current;
          return fn ? fn() : [];
        },
        removeLastExchange: () => {
          const rt = runtimeRef.current;
          if (!rt) return;
          const repo = rt.thread.export();
          if (repo.messages.length < 2) return;
          // Remove the last assistant message and its preceding user message
          const lastMsg = repo.messages[repo.messages.length - 1];
          const secondLast = repo.messages[repo.messages.length - 2];
          const isExchange =
            lastMsg?.message.role === 'assistant' &&
            secondLast?.message.role === 'user';
          if (!isExchange) return;
          const trimmed = repo.messages.slice(0, -2);
          rt.thread.import({
            headId: trimmed.length > 0 ? trimmed[trimmed.length - 1]!.message.id : null,
            messages: trimmed,
          });
        },
        restoreComposerText: (text: string) => {
          // Repopulate the composer (LexicalComposerInput is synced to the
          // assistant-ui composer state) and focus it so the user can
          // immediately edit/resend the cancelled message.
          const rt = runtimeRef.current;
          if (!rt) return;
          rt.thread.composer.setText(text);
          // Touch-first devices (tablets / Tesla): focusing would pop the
          // on-screen keyboard over the restored text — let the user tap.
          if (shouldAutoFocusInputs()) {
            requestAnimationFrame(() => {
              document.querySelector<HTMLElement>('.aui-lexical-input')?.focus();
            });
          }
        },
      }),
    }),
    [agentId, setConversationId, userTimezoneRef, isConnectedRef, subscribeRef, onActiveRunIdRef, dispatchRef],
  );

  const resolveToolName = useCallback(
    (id: string) => toolNameMapRef.current.get(id) ?? id,
    [],
  );

  const runtime = useLocalRuntime(adapter, { initialMessages });
  runtimeRef.current = runtime;
  return {
    runtime,
    conversationIdRef,
    activeRunIdStore,
    getToolName: resolveToolName,
    consumeAttachmentsRef,
  };
}
